import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export interface AgentNavigationCaptureRequest {
  id: string;
  status: 'pending' | 'ready' | 'creating';
  label?: string;
  startedAt: number;
  completedAt?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ViewportCaptureService {
  private readonly selector = '#ThreeJS canvas';
  private readonly agentNavigationStorageKey = 'agent_navigation_capture_request';
  private readonly maxPendingAgeMs = 10 * 60 * 1000;

  private readonly liveViewRequestedSubject = new Subject<AgentNavigationCaptureRequest>();
  /** Emits when an agent navigation has finished and the panel may create one live-view card. */
  readonly liveViewRequested$ = this.liveViewRequestedSubject.asObservable();

  async captureViewport(): Promise<string | null> {
    const canvas = this.getCanvas();

    if (!canvas) {
      console.warn('Viewport canvas not found for capture');
      return null;
    }

    try {
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Failed to capture viewport', error);
      return null;
    }
  }

  /**
   * Synchronous snapshot of the current canvas frame.
   * Used by the live-view dismiss path to capture the scene at the exact moment
   * the user closes the live view (guaranteed to be stable).
   */
  captureCurrentFrame(): string | null {
    const canvas = this.getCanvas();
    if (!canvas) {
      console.warn('[LiveView] Canvas not found for frame capture');
      return null;
    }
    try {
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('[LiveView] Failed to capture current frame', error);
      return null;
    }
  }

  beginAgentNavigationCapture(label?: string): void {
    this.saveAgentNavigationRequest({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      status: 'pending',
      label,
      startedAt: Date.now(),
    });
  }

  /**
   * Marks the active agent navigation as complete.
   * Existing callers still use requestLiveView(), but the card is only created
   * after a matching beginAgentNavigationCapture() from the agent flow.
   */
  requestLiveView(): void {
    const request = this.getAgentNavigationRequest();
    if (!request || request.status !== 'pending') {
      return;
    }

    if (Date.now() - request.startedAt > this.maxPendingAgeMs) {
      this.clearAgentNavigationRequest();
      return;
    }

    const readyRequest: AgentNavigationCaptureRequest = {
      ...request,
      status: 'ready',
      completedAt: Date.now(),
    };

    this.saveAgentNavigationRequest(readyRequest);
    this.liveViewRequestedSubject.next(readyRequest);
  }

  claimReadyAgentNavigationCapture(): AgentNavigationCaptureRequest | null {
    const request = this.getAgentNavigationRequest();
    if (!request || request.status !== 'ready') {
      return null;
    }

    const creatingRequest: AgentNavigationCaptureRequest = {
      ...request,
      status: 'creating',
    };
    this.saveAgentNavigationRequest(creatingRequest);
    return creatingRequest;
  }

  releaseAgentNavigationCapture(requestId: string): void {
    const request = this.getAgentNavigationRequest();
    if (!request || request.id !== requestId || request.status !== 'creating') {
      return;
    }
    this.saveAgentNavigationRequest({ ...request, status: 'ready' });
  }

  resolveAgentNavigationCapture(requestId: string): void {
    const request = this.getAgentNavigationRequest();
    if (!request || request.id !== requestId) {
      return;
    }
    this.clearAgentNavigationRequest();
  }

  private getCanvas(): HTMLCanvasElement | null {
    return document.querySelector<HTMLCanvasElement>(this.selector);
  }

  private getAgentNavigationRequest(): AgentNavigationCaptureRequest | null {
    try {
      const raw = sessionStorage.getItem(this.agentNavigationStorageKey);
      if (!raw) {
        return null;
      }
      const request = JSON.parse(raw) as AgentNavigationCaptureRequest;
      if (!request?.id || !request.status || !request.startedAt) {
        return null;
      }
      return request;
    } catch (error) {
      console.warn('[LiveView] Failed to read agent navigation capture request', error);
      return null;
    }
  }

  private saveAgentNavigationRequest(request: AgentNavigationCaptureRequest): void {
    try {
      sessionStorage.setItem(this.agentNavigationStorageKey, JSON.stringify(request));
    } catch (error) {
      console.warn('[LiveView] Failed to persist agent navigation capture request', error);
    }
  }

  private clearAgentNavigationRequest(): void {
    try {
      sessionStorage.removeItem(this.agentNavigationStorageKey);
    } catch (error) {
      console.warn('[LiveView] Failed to clear agent navigation capture request', error);
    }
  }
}
